/**
 * @copyright Copyright 2021-2024 Epic Systems Corporation
 * @file One row representing one app in the In Progress Apps widget
 * @module Epic.AppOrchard.Dashboard.InProgressAppRow
 */

import { StarIcon } from "@chakra-ui/icons";
import { Box, Image, List, ListItem } from "@chakra-ui/react";
import { IDispatch } from "@epic/react-redux-booster";
import {
	ContactButton,
	DownloadBuildIcon,
	DownloadBuildLink,
	EditableInstallStatus,
	ImplementationGuideLink,
	NewVersionLink,
} from "ao/components/Core";
import { badgerAuthor } from "ao/data";
import { DownloadApprovedStatus, InstallStatus } from "ao/data/types";
import { useSiteInfoState } from "ao/state/siteInfo";
import { updateInstallStatusInStore } from "ao/store-methods/inProgressApps";
import { clearLoaded } from "ao/store-methods/liveApps";
import { IInProgressApp, IUserSecurity } from "ao/types";
import React, { FC, memo, useCallback, useState } from "react";
import { WidgetActionButton, WidgetAppRow, WidgetTag } from "../../../widgetHelpers";
import { DownloadApprovedInfo } from "./DownloadApprovedInfo";

interface InProgressAppRowProps {
	app: IInProgressApp;
	userSecurity: IUserSecurity;
	baseUrl: string;
	orgId: number;
	dispatch: IDispatch;
}

/** One row representing one app in the In Progress Apps widget */
export const InProgressAppRow: FC<InProgressAppRowProps> = memo((props: InProgressAppRowProps) => {
	const { userSecurity, app, baseUrl, orgId, dispatch } = props;

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const marketplaceUrl = `${siteInfo.showroomUrl}Listing?appId=`;
	const downloadUrl = `/Download?id=`;
	
	const tesseractLogo = `${baseUrl}Content/images/Tesseract_Logo.png`;
	const isNewestAppVersion = app.appId === app.newestAppVersion;
	const isUnapproved = app.downloadApprovedStatus === DownloadApprovedStatus.Unapproved;
	const hasTcPackages = app.turboChargerDocumentIds.length > 0;
	const hasBottomContent =
		(hasTcPackages && !isUnapproved) || !isNewestAppVersion || app.implementationGuideUrl;

	const downloadPageUrl = (app.isAvailableInGallery ? marketplaceUrl : downloadUrl) + app.appId;
	const newestVersionDownloadPageUrl =
		(app.isNewestVersionAvailableInGallery ? marketplaceUrl : downloadUrl) + app.newestAppVersion;

	// used to focus the app link after the contact button after contacting since the contact button will be disabled
	const [appLinkRef, setAppLinkRef] = useState<React.RefObject<HTMLAnchorElement> | null>(null);

	const handleSetAppLinkRef = useCallback(
		(linkRef: React.RefObject<HTMLAnchorElement> | null) => setAppLinkRef(linkRef),
		[],
	);
	const handleUpdateInstallStatusSuccess = useCallback(
		(newStatus: InstallStatus) => {
			updateInstallStatusInStore(dispatch, app.appId, orgId, newStatus);

			// for live apps widget to reload its apps after this new addition
			if (newStatus === InstallStatus.Live) {
				clearLoaded(dispatch, orgId);
			}
		},
		[app.appId, dispatch, orgId],
	);

	return (
		<>
			<WidgetAppRow
				appId={app.appId}
				appName={app.appName}
				subtitle={<Box title="The organization that created this app">{app.appOrgName}</Box>}
				linkUrl={downloadPageUrl}
				onLinkRefCreated={handleSetAppLinkRef}
				linkTitle="Open this app's listing"
				belowLogoContent={
					<>
						{app.tesseractEnabled && (
							<Image
								src={tesseractLogo}
								h="18px"
								w="18px"
								title="This app is enabled for Tesseract."
								aria-label="This app is enabled for Tesseract"
								alt="Tesseract logo"
								display="inline-block"
							/>
						)}

						{hasTcPackages && <DownloadBuildIcon />}
					</>
				}
				additionalAppInfo={
					<List spacing="0.3em" display="inline-block">
						{isUnapproved ? (
							<ListItem>
								<WidgetTag
									label="Download requested"
									tooltipContent={
										"Download requested by " +
										app.downloadRequest.userName +
										" on " +
										app.downloadRequest.instant.toLocaleDateString()
									}
									icon={StarIcon}
								></WidgetTag>
							</ListItem>
						) : (
							<ListItem>
								<DownloadApprovedInfo
									nonProdLicensedOn={app.nonProdLicensedOn}
									prodLicensedOn={app.prodLicensedOn}
									enabledForProdOnly={app.enabledForProdOnly || false}
								></DownloadApprovedInfo>
							</ListItem>
						)}
					</List>
				}
				bottomContent={
					hasBottomContent && (
						<>
							{app.implementationGuideUrl && (
								<ImplementationGuideLink url={app.implementationGuideUrl} />
							)}
							{hasTcPackages && !isUnapproved && (
								<DownloadBuildLink appId={app.appId} showIcon></DownloadBuildLink>
							)}
							{!isNewestAppVersion && (
								<NewVersionLink
									newVersionUrl={newestVersionDownloadPageUrl}
									showWarningIcon
									appId={app.appId}
									newestAppId={app.newestAppVersion!}
								/>
							)}
						</>
					)
				}
				actions={
					userSecurity.canPurchase && (
						<>
							{isUnapproved && (
								<ContactButton
									buttonAs={WidgetActionButton}
									contactApi={badgerAuthor}
									apiArgs={[app.appId]}
									buttonCaption="Request Update"
									title={`Request Update from ${app.appOrgName ?? app.appName}`}
									buttonNotContactedHelptext="Send an email to the vendor requesting an update on your pending download request."
									successMessage="An email was sent to the vendor."
									errorMessage="Error contacting the vendor. Please try again later or contact your Epic representative if this continues to happen."
									confirmModalTitle={`Request Update from ${app.appOrgName ?? app.appName}`}
									confirmModalPrompt={`This will send an email to ${
										app.appOrgName ?? app.appName
									} requesting an update on your pending download request. Are you sure you want to continue?`}
									finalFocusRef={appLinkRef || undefined}
								/>
							)}
							{
								<EditableInstallStatus
									successHandler={handleUpdateInstallStatusSuccess}
									installStatus={app.installStatus}
									downloadApprovedStatus={app.downloadApprovedStatus}
									appId={app.appId}
									orgId={orgId}
									buttonsAs={WidgetActionButton}
									deactivationWarning="Are you sure you want to set the install status for this app to deactivated? If you continue the app will be removed from this dashboard. You will still be able to find the app and update the install status from Picked Apps and from the app's listing."
								/>
							}
						</>
					)
				}
			></WidgetAppRow>
		</>
	);
});
